import { Vec3, vec3 } from '../math/vec3'

export type PartCategory =
  | 'command' | 'tank' | 'engine' | 'decoupler'
  | 'aero' | 'structural' | 'parachute' | 'heatshield' | 'leg' | 'rcs'

/** Forma geometrica, usata sia per l'inerzia sia per il rendering. +Y e l'asse del razzo. */
export type Shape =
  | { readonly kind: 'cylinder'; readonly radius: number; readonly height: number }
  | { readonly kind: 'cone'; readonly radius: number; readonly height: number }
  | { readonly kind: 'box'; readonly x: number; readonly y: number; readonly z: number }

export interface AttachNode {
  readonly id: string
  /** posizione nel sistema locale della parte */
  readonly position: Vec3
  /** direzione verso l'esterno */
  readonly direction: Vec3
  /** diametro nominale: due nodi si agganciano solo a taglie compatibili */
  readonly size: number
  readonly kind: 'axial' | 'radial'
}

export interface EngineDef {
  readonly thrustVac: number
  readonly thrustSl: number
  readonly ispVac: number
  readonly ispSl: number
  /** escursione massima del gimbal, in radianti */
  readonly gimbalRange: number
  readonly throttleable: boolean
}

export interface AeroSurfaceDef {
  /** pendenza della curva di portanza, per radiante */
  readonly liftSlope: number
  /** superficie di riferimento, m² */
  readonly area: number
  /** angolo di stallo, in radianti */
  readonly stallAngle: number
  /** normale della superficie nel sistema locale della parte */
  readonly normal: Vec3
  /** true se e una superficie mobile comandata dal pilota */
  readonly controllable: boolean
}

/**
 * Proprieta strutturali del guscio. Il valore che conta e lo SPESSORE: un
 * razzo e un guscio sottile, e cede per instabilita molto prima che per
 * snervamento.
 */
export interface Materiale {
  /** modulo di Young, Pa */
  readonly modulus: number
  /** tensione di snervamento, Pa */
  readonly yieldStrength: number
  /** spessore di parete, m */
  readonly wallThickness: number
}

/** Alluminio aerospaziale, parete sottile. */
export const ALLUMINIO: Materiale = {
  modulus: 70e9,
  yieldStrength: 270e6,
  wallThickness: 0.004,
}

export interface PartDef {
  readonly id: string
  readonly name: string
  readonly category: PartCategory
  /** massa a vuoto, kg */
  readonly dryMass: number
  readonly shape: Shape
  readonly nodes: readonly AttachNode[]
  /** coefficiente di resistenza */
  readonly cd: number
  /** superficie frontale di riferimento, m² */
  readonly refArea: number
  /** temperatura di distruzione, K */
  readonly maxTemp: number
  /**
   * Coefficiente di flusso trasversale (Allen e Perkins). Default 1.2, che e
   * la resistenza di un cilindro investito di traverso. Zero per le superfici
   * portanti, che hanno gia il proprio modello: sommare i due termini
   * conterebbe la stessa forza due volte.
   */
  readonly crossflowFactor?: number
  /** materiale del guscio; se manca vale l'alluminio */
  readonly material?: Materiale
  /** punto d'appoggio di una gamba, nel sistema locale della parte */
  readonly footPoint?: Vec3
  readonly tank?: { readonly propellantMass: number }
  readonly engine?: EngineDef
  readonly aeroSurface?: AeroSurfaceDef
  readonly decoupler?: { readonly ejectionForce: number; readonly crossfeed: boolean }
  readonly parachute?: {
    /** superficie a paracadute aperto, m² */
    readonly areaAperto: number
    /** superficie in apertura parziale (drogue), m² */
    readonly areaParziale: number
    /** pressione dinamica oltre la quale si strappa, Pa */
    readonly pressioneStrappo: number
    /** quota sotto la quale si apre del tutto, m */
    readonly quotaApertura: number
  }
  readonly heatShield?: {
    /** massa ablativa, kg */
    readonly ablativo: number
    /** calore assorbito per kg di ablativo, J/kg */
    readonly capacita: number
  }
}

/** Superficie frontale di un cilindro o cono di raggio r. */
export const circleArea = (r: number): number => Math.PI * r * r

export const axialNodes = (halfHeight: number, size: number): AttachNode[] => [
  { id: 'top', position: vec3(0, halfHeight, 0), direction: vec3(0, 1, 0), size, kind: 'axial' },
  { id: 'bottom', position: vec3(0, -halfHeight, 0), direction: vec3(0, -1, 0), size, kind: 'axial' },
]
